import { useState, useEffect } from 'react';
import api from '../../services/api';
import { MegaphoneIcon, PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    position: 'top',
    button_link: '', // renamed from link_url
    button_text: '', // renamed from link_text
    coupon_code: '',
    target_pages: [],
    starts_at: '',
    expires_at: '',
    is_active: true,
    dismissible: true, // renamed from is_dismissible
    background_color: '#2563eb', // sensible default blue
    text_color: '#ffffff',
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const response = await api.get('/admin/banners');
      setBanners(response.data);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = { ...formData };
      // Normalize empty strings to null for optional fields
      ['button_link','button_text','coupon_code','starts_at','expires_at','background_color','text_color'].forEach(key => {
        if (data[key] === '') data[key] = null;
      });
      if (data.target_pages.length === 0) data.target_pages = null;

      if (editingBanner) {
        await api.put(`/admin/banners/${editingBanner.id}`, data);
      } else {
        await api.post('/admin/banners', data);
      }

      loadBanners();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving banner');
    }
  };

  const deleteBanner = async (id) => {
    if (!confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await api.delete(`/admin/banners/${id}`);
      loadBanners();
    } catch (error) {
      alert('Error deleting banner');
    }
  };

  const editBanner = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      message: banner.message,
      type: banner.type,
      position: banner.position,
      button_link: banner.button_link || '',
      button_text: banner.button_text || '',
      coupon_code: banner.coupon_code || '',
      target_pages: banner.target_pages || [],
      starts_at: banner.starts_at ? banner.starts_at.slice(0, 16) : '',
      expires_at: banner.expires_at ? banner.expires_at.slice(0, 16) : '',
      is_active: banner.is_active,
      dismissible: banner.dismissible,
      background_color: banner.background_color || '#2563eb',
      text_color: banner.text_color || '#ffffff',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingBanner(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      position: 'top',
      button_link: '',
      button_text: '',
      coupon_code: '',
      target_pages: [],
      starts_at: '',
      expires_at: '',
      is_active: true,
      dismissible: true,
      background_color: '#2563eb',
      text_color: '#ffffff',
    });
  };

  const togglePage = (page) => {
    const pages = formData.target_pages.includes(page)
      ? formData.target_pages.filter(p => p !== page)
      : [...formData.target_pages, page];
    setFormData({ ...formData, target_pages: pages });
  };

  const typeColors = {
    info: 'bg-blue-100 text-blue-800 border-blue-200',
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    error: 'bg-red-100 text-red-800 border-red-200',
    promotional: 'bg-purple-100 text-purple-800 border-purple-200',
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MegaphoneIcon className="h-8 w-8 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Banner Management</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Banner
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingBanner ? 'Edit Banner' : 'Create New Banner'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Black Friday Sale!"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Get 40% off all courses this weekend only!"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="info">Info (Blue)</option>
                  <option value="success">Success (Green)</option>
                  <option value="warning">Warning (Yellow)</option>
                  <option value="error">Error (Red)</option>
                  <option value="promotional">Promotional (Purple)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position *</label>
                <select
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="top">Top of Page</option>
                  <option value="bottom">Bottom of Page</option>
                  <option value="modal">Modal Popup</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                <input
                  type="text"
                  value={formData.button_text}
                  onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Shop Now"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Link URL</label>
                <input
                  type="url"
                  value={formData.button_link}
                  onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/sale"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                <input
                  type="text"
                  value={formData.coupon_code}
                  onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="SAVE40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Starts At</label>
                <input
                  type="datetime-local"
                  value={formData.starts_at}
                  onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
                <input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Pages (leave empty for all)</label>
                <div className="flex flex-wrap gap-2">
                  {['home', 'modules', 'labs', 'capstones', 'checkout', 'dashboard', 'login', 'register'].map(page => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => togglePage(page)}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                        formData.target_pages.includes(page)
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-blue-600'
                      }`}
                    >
                      {page.charAt(0).toUpperCase() + page.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.dismissible}
                    onChange={(e) => setFormData({ ...formData, dismissible: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Dismissible</span>
                </label>

                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Background</label>
                  <input
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Text</label>
                  <input
                    type="color"
                    value={formData.text_color}
                    onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
              <div className={`p-4 rounded-lg border-2 ${typeColors[formData.type]}`}
                style={{ backgroundColor: formData.background_color, color: formData.text_color }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{formData.title || 'Your Title Here'}</h4>
                    <p className="text-sm mt-1">{formData.message || 'Your message here'}</p>
                    {formData.button_link && formData.button_text && (
                      <button className="mt-2 text-sm font-medium underline">
                        {formData.button_text}
                      </button>
                    )}
                    {formData.coupon_code && (
                      <span className="mt-2 inline-block text-xs font-mono bg-white/20 rounded px-2 py-1">{formData.coupon_code}</span>
                    )}
                  </div>
                  {formData.dismissible && (
                    <button type="button" className="text-gray-200 hover:text-white">
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingBanner ? 'Update' : 'Create'} Banner
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stats</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {banners.map(banner => (
              <tr key={banner.id} className={!banner.is_active ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{banner.title}</div>
                  <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">{banner.message}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[banner.type]}`}>
                    {banner.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {banner.position}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <EyeIcon className="h-4 w-4" />
                    <span>{banner.view_count || 0} views</span>
                    {banner.dismissible && (
                      <span className="text-xs">• {banner.dismissal_count || 0} dismissed</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                  {banner.starts_at && <div>From: {new Date(banner.starts_at).toLocaleDateString()}</div>}
                  {banner.expires_at && <div>To: {new Date(banner.expires_at).toLocaleDateString()}</div>}
                  {!banner.starts_at && !banner.expires_at && <div className="text-gray-400">Always</div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    banner.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {banner.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => editBanner(banner)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => deleteBanner(banner.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {banners.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No banners created yet. Create your first promotional banner!
          </div>
        )}
      </div>
    </div>
  );
};

export default BannerManagement;
